import React, { useMemo, useState } from 'react';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext';
import { ReactComponent as NoPotentialSavings } from 'recommendationsNew/img/savingOpportunitiesEmpty.svg';
import Spinner from 'shared/components/andtComponents/Spinner';
import InfoPopover from 'shared/components/andtComponents/InfoPopover';
import CustomCSVDownload from 'shared/components/buttons/CustomCSVDownload';
import { FILTERS, GROUP_BY_OPTIONS, SORT_BY_OPTIONS, WASTE_TAB_KEYS } from 'recommendationsNew/consts';
import { useRecommendationsNewContext } from 'recommendationsNew/contexts/recommendationsNewContext';
import useGroupByOption from 'recommendationsNew/hooks/react-query/useGroupByOption';
import SavingsGrid from 'recommendationsNew/components/heatMap/cloudWaste/savingsGrid.jsx';
import { useHeatMapContext } from 'recommendationsNew/components/heatMap/contexts/heatMapContext.jsx';
import classes from './cloudWaste.module.scss';

const exportColumns = [
  { key: 'type', label: 'Type' },
  { key: 'savings', label: 'Potential Savings' },
  { key: 'percentOfTotal', label: '% of Total' },
  { key: 'numberOfRecs', label: 'No.' },
];

const SavingsOpportunitiesTable = ({ externalData, overrideCurrency }) => {
  const { getCurrencyNumber } = useUserSettingsContext();
  const {
    recommendationFilters: filtersContext,
    recommendationExternalFilters: externalFiltersContext,
    isDashboardPanel,
  } = useRecommendationsNewContext();
  const { wasteTabSelected } = useHeatMapContext();
  const isWasteChart = wasteTabSelected === WASTE_TAB_KEYS.wasteChart;

  const [columnsWidth, setColumnsWidth] = useState(undefined);

  const filtersNoTypeId = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { [FILTERS.TYPES.id]: typeId, ...rest } = filtersContext;
    return rest;
  }, [filtersContext]);

  const recommendationsGroupTypeHook = useGroupByOption(!externalData);
  const { data: recommendationsGroupTypeData, isLoading: isRecGroupByTypeLoading } =
    recommendationsGroupTypeHook.fetchGroupByOption({
      filters: filtersNoTypeId,
      groupBy: GROUP_BY_OPTIONS.items.TYPE_ID.id,
      pageSize: 100,
      sortBy: SORT_BY_OPTIONS.items.SAVINGS.property,
      externalFilters: externalFiltersContext,
    });

  const totalsRow = useMemo(
    () => ({
      potentialAnnualSavings: (externalData?.page || recommendationsGroupTypeData?.page)?.reduce(
        (acc, curr) => acc + curr.savings,
        0,
      ),
      annualCostRecsUnimplemented: (externalData?.page || recommendationsGroupTypeData?.page)?.reduce(
        (acc, curr) => acc + curr.current,
        0,
      ),
      totalRecommendationsCount: (externalData?.page || recommendationsGroupTypeData?.page)?.reduce(
        (acc, curr) => acc + curr.recsCount,
        0,
      ),
    }),
    [recommendationsGroupTypeData, externalData],
  );

  const savingOpportunitiesTooltip = (
    <span>
      The percentage is calculated by dividing the <i>Potential savings cost</i> by the <i>Total actual cost</i>
    </span>
  );

  const fetchDataForExport = () => {
    const exportData = externalData?.page || recommendationsGroupTypeData?.page; // prepare colum
    // prepare data for export
    const dataToExport = (exportData || []).map((rec) => {
      const { label, savings, current, recsCount } = rec;
      return {
        type: label,
        savings: getCurrencyNumber(savings, 2, {}, overrideCurrency),
        percentOfTotal:
          savings && current ? `${((savings * 100) / totalsRow?.annualCostRecsUnimplemented).toFixed(2)}%` : 0,
        numberOfRecs: recsCount || 0,
      };
    });
    const totalRow = [
      {
        type: 'Total',
        savings: getCurrencyNumber(totalsRow?.potentialAnnualSavings, 2, {}, overrideCurrency),
        percentOfTotal:
          totalsRow?.potentialAnnualSavings && totalsRow?.annualCostRecsUnimplemented
            ? `${((totalsRow?.potentialAnnualSavings * 100) / totalsRow?.annualCostRecsUnimplemented).toFixed(2)}%`
            : `0%`,
        numberOfRecs: totalsRow?.totalRecommendationsCount,
      },
    ];
    return [
      {
        data: [...(totalRow ?? []), ...(dataToExport ?? [])],
        filename: 'Savings Opportunities.csv',
        headers: [...exportColumns],
      },
    ];
  };

  return (
    <div
      className={`${classes.savingsOpportunitiesContainer} ${isDashboardPanel && classes.dashboardContainer}`}
      ref={(node) => {
        if (node && !columnsWidth) {
          const tableWidth = node.offsetWidth;
          setColumnsWidth([
            { columnName: 'type', width: tableWidth * 0.4 },
            { columnName: 'savings', width: tableWidth * 0.26 },
            { columnName: 'percentOfTotal', width: tableWidth * 0.2 },
            { columnName: 'numberOfRecs', width: tableWidth * 0.14 },
          ]);
        }
      }}
    >
      <div className={classes.titleWrapper}>
        <div className={classes.title}>
          {isWasteChart ? (
            <div>
              Savings Opportunities <span className={classes.subTitle}>(for Open recommendations only)</span>
            </div>
          ) : (
            <div>
              Savings <span className={classes.subTitle}> (for closed & done recommendations)</span>
            </div>
          )}
          {isWasteChart && <InfoPopover>{savingOpportunitiesTooltip}</InfoPopover>}
        </div>
        <CustomCSVDownload fetchData={fetchDataForExport} isLoading={false} showDownloadIcon hideText filesNumber={1} />
      </div>
      <div className={classes.summaryTable} id="saving-opportunities">
        {!externalData && (isRecGroupByTypeLoading || !recommendationsGroupTypeData || !columnsWidth) ? (
          <Spinner />
        ) : (
          <>
            <SavingsGrid
              data={externalData?.page || recommendationsGroupTypeData?.page}
              columnsWidth={columnsWidth}
              setColumnsWidth={setColumnsWidth}
              isLoading={isRecGroupByTypeLoading}
              totalsRow={totalsRow}
            />
            {!recommendationsGroupTypeData?.page?.length && !externalData?.page?.length && (
              <div className={classes.noPotentialSavingsContainer}>
                <NoPotentialSavings />
                <div className={classes.noPotentialSavingsText}>
                  <div>No Potential Savings found in this view</div>
                  <div className={classes.noPotentialSavingsSubText}>Try different filters</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SavingsOpportunitiesTable;
